import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Gamepad2,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  ExternalLink,
  Trash2,
  Download,
  Sparkles,
  Film,
  PlayCircle,
  Copy,
  Link2
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Canal Gaming» (solo admin): conexión del SEGUNDO canal de YouTube y sus Shorts. Solo /api/admin/gaming/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/gaming`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  writing: { label: "Escribiendo guion", style: "bg-violet-500/15 text-violet-300 border-violet-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-violet-500/15 text-violet-300 border-violet-500/30", busy: true },
  uploading: { label: "Subiendo", style: "bg-violet-500/15 text-violet-300 border-violet-500/30", busy: true },
  ready: { label: "Listo, sin subir", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "Publicado", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};

const CATEGORIES = {
  curiosidades: "Curiosidades", consolas: "Consolas", pc: "PC gaming", lanzamientos: "Lanzamientos",
  noticias: "Noticias", retro: "Retro e historia", trucos: "Trucos y consejos"
};

export const GamingChannelView = () => {
  const [status, setStatus] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState({ client_id: "", client_secret: "" });
  const [create, setCreate] = useState({ topic: "", category: "curiosidades", upload: true });
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/channel`, { params: { origin: window.location.origin }, ...opts() });
      setStatus(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar el canal gaming." });
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadVideos();
    const onMsg = (ev) => {
      if (ev.data?.type === "GAMING_YT_CONNECTED") {
        setWorking(null);
        setFeedback({ type: "success", text: ev.data.message || "Canal gaming conectado." });
        loadStatus();
      } else if (ev.data?.type === "GAMING_YT_ERROR") {
        setWorking(null);
        setFeedback({ type: "error", text: ev.data.message || "Google rechazó la conexión." });
        loadStatus();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [loadStatus, loadVideos]);

  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy);
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadStatus(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadStatus]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      await fn();
      if (okText) setFeedback({ type: "success", text: okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const saveCreds = () => run("creds", async () => {
    const res = await axios.post(`${API}/channel/credentials`, creds, opts());
    setStatus(res.data);
    setCreds({ client_id: "", client_secret: "" });
  }, "Credenciales guardadas. Ahora pulsa «Conectar canal gaming».");

  const connect = async () => {
    setFeedback(null);
    setWorking("connect");
    try {
      const res = await axios.get(`${API}/channel/auth-url`, { params: { origin: window.location.origin }, ...opts() });
      const popup = window.open(res.data.auth_url, "cortoviral_gaming_oauth", "width=650,height=780,left=200,top=80,resizable=yes,scrollbars=yes");
      if (!popup) {
        window.location.href = res.data.auth_url;
        return;
      }
      setFeedback({ type: "info", text: "En la ventana de Google elige el canal de GAMING (no el principal). Al terminar, esta pantalla se actualiza sola." });
      const t = setInterval(() => {
        if (popup.closed) {
          clearInterval(t);
          setWorking(null);
          loadStatus();
        }
      }, 800);
    } catch (e) {
      setWorking(null);
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error iniciando la conexión con Google." });
    }
  };

  const disconnect = () => {
    if (!window.confirm("¿Desconectar el canal gaming? Las credenciales del proyecto se conservan para volver a conectar.")) return;
    run("disconnect", async () => {
      const res = await axios.post(`${API}/channel/disconnect`, {}, opts());
      setStatus(res.data);
    }, "Canal gaming desconectado.");
  };

  const forgetCreds = () => {
    if (!window.confirm("¿Borrar también el Client ID y el Client Secret guardados?")) return;
    run("forget", async () => {
      const res = await axios.post(`${API}/channel/forget-credentials`, {}, opts());
      setStatus(res.data);
    }, "Credenciales borradas.");
  };

  const createVideo = () => run("create", async () => {
    await axios.post(`${API}/videos/create`, create, opts());
    setCreate((c) => ({ ...c, topic: "" }));
    setBusy(true);
    loadVideos();
  }, "Creando el Short. Aparecerá abajo en un par de minutos.");

  const uploadVideo = (v) => run(`up-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/upload`, {}, opts());
    loadVideos();
    loadStatus();
  }, "Short subido al canal gaming.");

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en YouTube, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  const copyUri = () => {
    navigator.clipboard.writeText(status.redirect_uri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!status) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando canal gaming...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const fbStyle = feedback?.type === "success"
    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
    : feedback?.type === "info" ? "bg-violet-500/10 border border-violet-500/30 text-violet-200"
      : "bg-red-500/10 border border-red-500/30 text-red-300";

  return (
    <div data-testid="gaming-channel-view" className="space-y-6">
      <div className="bg-gradient-to-br from-[#12141D] to-[#1A1230] border border-violet-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {status.connected && status.channel_thumbnail ? (
              <img src={status.channel_thumbnail} alt="" className="w-12 h-12 rounded-xl border border-violet-500/40 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/40 text-violet-300 flex items-center justify-center shrink-0">
                <Gamepad2 className="w-6 h-6" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Canal Gaming</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">Solo admin</span>
                {status.connected ? (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                    Conectado: {status.channel_title}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Tu segundo canal de YouTube, solo de videojuegos. Va aparte del canal principal: su propia conexión con Google,
                su propia cuota y sus propios Shorts (no salen en «Mis Shorts»). Si intentas conectar aquí el canal principal, se rechaza.
              </p>
            </div>
          </div>
          {status.connected && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">Subidas hoy: {status.uploads_today}/{status.max_uploads}</span>
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${fbStyle}`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}

        {status.same_project_as_main && (
          <div className="p-3 rounded-xl text-xs flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-200">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Estas credenciales son las mismas que usa el canal principal: compartirán la cuota diaria. Lo recomendable es un proyecto de Google Cloud aparte.</span>
          </div>
        )}
        {status.last_error && !status.connected && (
          <p className="text-[11px] text-red-400 break-words">Último error: {status.last_error}</p>
        )}

        {!status.connected && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-violet-300" /> Proyecto de Google Cloud del canal gaming</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>Crea un proyecto NUEVO en Google Cloud (no uses el del canal principal) y activa «YouTube Data API v3».</li>
                <li>Pantalla de consentimiento: tipo Externo y, al acabar, pulsa <strong className="text-slate-200">Publicar app / En producción</strong> (en «Prueba» la conexión caduca cada 7 días).</li>
                <li>Credenciales → ID de cliente OAuth → Aplicación web, con esta URI de redirección:</li>
              </ol>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate bg-black/50 border border-white/10 rounded px-2 py-1 text-[11px] text-violet-200">{status.redirect_uri}</code>
                <Button size="sm" variant="ghost" onClick={copyUri} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                  <Copy className="w-3 h-3" /> {copied ? "Copiada" : "Copiar"}
                </Button>
              </div>
              <p className="text-slate-400">4. Pega aquí el Client ID y el Client Secret y pulsa «Conectar canal gaming».</p>
            </div>
            <div className="space-y-2">
              {status.has_credentials ? (
                <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2">
                  <p>Credenciales guardadas: <code className="text-violet-200">{status.client_id_masked}</code></p>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="gaming-connect-btn" onClick={connect} disabled={working !== null}
                      className="bg-violet-500 hover:bg-violet-400 text-white font-black text-xs flex items-center gap-1.5">
                      {working === "connect" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                      Conectar canal gaming
                    </Button>
                    <Button variant="ghost" size="sm" onClick={forgetCreds} disabled={working !== null}
                      className="h-9 text-xs text-slate-400 hover:text-red-300">Cambiar credenciales</Button>
                  </div>
                  <p className="text-[10px] text-amber-300">En la pantalla de Google, elige el canal de GAMING de tu cuenta.</p>
                </div>
              ) : (
                <>
                  <input type="text" placeholder="Client ID (….apps.googleusercontent.com)" value={creds.client_id} autoComplete="off"
                    onChange={(e) => setCreds((c) => ({ ...c, client_id: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/60" />
                  <input type="password" placeholder="Client Secret" value={creds.client_secret} autoComplete="new-password"
                    onChange={(e) => setCreds((c) => ({ ...c, client_secret: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/60" />
                  <Button data-testid="gaming-save-creds-btn" onClick={saveCreds} disabled={working !== null || !creds.client_id || !creds.client_secret}
                    className="w-full bg-violet-500 hover:bg-violet-400 text-white font-black text-xs flex items-center justify-center gap-1.5">
                    {working === "creds" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Guardar credenciales
                  </Button>
                  <p className="text-[10px] text-slate-500">Se guardan solo en el servidor y nunca se devuelven al navegador.</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3">
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-300" /> Crear un Short de gaming sobre un tema</h4>
        <div className="flex flex-col lg:flex-row gap-2">
          <input type="text" maxLength={200} placeholder="Por ejemplo: por qué la PS2 sigue siendo la consola más vendida"
            value={create.topic} onChange={(e) => setCreate((c) => ({ ...c, topic: e.target.value }))}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/60" />
          <select value={create.category} onChange={(e) => setCreate((c) => ({ ...c, category: e.target.value }))}
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
            {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-300 px-1">
            <input type="checkbox" checked={create.upload} onChange={(e) => setCreate((c) => ({ ...c, upload: e.target.checked }))} className="accent-violet-500 w-4 h-4" />
            Subir al terminar
          </label>
          <Button data-testid="gaming-create-btn" onClick={createVideo} disabled={working !== null || busy || create.topic.trim().length < 5}
            title={busy ? "Ya se está creando un Short; espera a que termine" : ""}
            className="bg-violet-500 hover:bg-violet-400 text-white font-black text-xs flex items-center gap-1.5">
            {working === "create" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
            Crear Short
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">Se hace uno a la vez. Si se está montando un Short del canal principal, el de gaming espera su turno.</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-black text-white">Shorts del canal gaming ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={loadVideos} className="h-7 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${anyBusy ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
        {videos.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            Todavía no hay Shorts de gaming. Crea uno arriba o desde el Radar Gaming.
          </div>
        ) : (
          <div className="space-y-2">
            {videos.map((v) => {
              const st = STATUS[v.status] || STATUS.failed;
              const fileUrl = `${API}/videos/${v.id}/file`;
              const hasFile = v.video_url && !v.video_deleted;
              return (
                <div key={v.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 space-y-2 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${st.style}`}>
                          {st.busy && <RefreshCw className="w-2.5 h-2.5 inline mr-1 animate-spin" />}{st.label}
                        </span>
                        <span className="text-[10px] uppercase font-bold text-violet-300">{CATEGORIES[v.category] || v.category}</span>
                        <span className="text-[10px] text-slate-500">{v.source === "radar" ? "Del Radar" : "Manual"} · {new Date(v.created_at).toLocaleString("es-ES")}</span>
                        {v.privacy && v.status === "published" && <span className="text-[10px] text-slate-500">· {v.privacy}</span>}
                      </div>
                      <p className="text-sm font-bold text-white break-words">{v.title}</p>
                      {v.progress && <p className={`text-[11px] break-words ${v.status === "failed" ? "text-red-400" : "text-slate-400"}`}>{v.progress}</p>}
                      {v.trend_title && <p className="text-[11px] text-slate-500 truncate">Tendencia de origen: {v.trend_title}</p>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {hasFile && (
                        <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)}
                          className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <PlayCircle className="w-3 h-3" /> {preview === v.id ? "Ocultar" : "Ver"}
                        </Button>
                      )}
                      {hasFile && (
                        <a href={fileUrl} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 rounded-md hover:bg-white/5">
                          <Download className="w-3 h-3" /> Descargar
                        </a>
                      )}
                      {v.status === "ready" && hasFile && (
                        <Button size="sm" onClick={() => uploadVideo(v)} disabled={working !== null || !status.connected}
                          title={status.connected ? "" : "Conecta el canal gaming"}
                          className="h-7 px-2.5 text-[11px] font-bold bg-violet-500 hover:bg-violet-400 text-white flex items-center gap-1">
                          {working === `up-${v.id}` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          Subir al canal gaming
                        </Button>
                      )}
                      {v.yt_url && (
                        <a href={v.yt_url} target="_blank" rel="noopener noreferrer"
                          className="h-7 px-2.5 text-[11px] font-bold text-emerald-300 hover:text-emerald-200 flex items-center gap-1 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                          <ExternalLink className="w-3 h-3" /> Ver en YouTube
                        </a>
                      )}
                      {!st.busy && (
                        <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null}
                          className="h-7 px-2 text-[11px] text-slate-500 hover:text-red-300 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {preview === v.id && hasFile && (
                    <video src={fileUrl} controls className="w-full max-w-[280px] aspect-[9/16] rounded-lg bg-black" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
